/**
 *
 * OpenAppBanner — banner "Dùng app" trên cùng mobile web + popup lần add giỏ đầu tiên
 * Figma: Improve tab suggest open app (16398:109515)
 *
 */

import { CloseOutlined } from '@ant-design/icons';
import { TopNavWrapper } from 'components/MainNav/TopNavComponents';
import { Viewport } from 'components/ViewportProvider';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styles/styled-components';
import { openApp } from 'utils/detechMobileDevice';
import messages from './messages';
import { APP_BANNER_HEIGHT, OPEN_APP_POPUP_EVENT, dismissAppBanner, shouldShowAppBanner } from './openAppPrompt';

const GREEN = '#43A047';

const Bar = styled.div<{ $green: boolean }>`
  height: ${APP_BANNER_HEIGHT}px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $green }) => ($green ? GREEN : '#ffffff')};
  color: ${({ $green }) => ($green ? '#ffffff' : '#111827')};
  border-bottom: ${({ $green }) => ($green ? '1px solid rgba(255,255,255,0.10)' : '1px solid #f2f3f5')};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Texts = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div<{ $green: boolean }>`
  font-size: 10px;
  line-height: 14px;
  color: ${({ $green }) => ($green ? '#ffffff' : '#4B5563')};
`;

const Cta = styled.button<{ $green: boolean }>`
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  cursor: pointer;
  background: ${({ $green }) => ($green ? '#ffffff' : GREEN)};
  color: ${({ $green }) => ($green ? '#000' : '#ffffff')};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const PopupBox = styled.div`
  width: 343px;
  max-width: calc(100vw - 32px);
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
`;

const PopupTitle = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #111827;
`;

const PopupActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const PopupButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #007aff;
  cursor: pointer;
`;

// Banner + header đều position: fixed, cuộn qua BANNER_H thì banner trượt lên ẩn (CSS transition trên GPU).
// Vì sao không cho banner trôi theo nội dung (sticky/absolute): Safari iOS chỉ lấy lại màu status bar khi
// một phần tử fixed/sticky CHẠM mép trên, không lấy lại khi nó rời đi → cuộn về đầu trang vẫn kẹt màu
// trắng của header. Fixed + trượt thì mép trên lúc nào cũng là 1 phần tử fixed có màu rõ ràng.
const SLIDE = '0.2s ease';

const FixedBar = styled.div<{ $shown: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  // dưới overlay sidebar mobile (999) để mở sidebar thì banner cũng bị phủ tối
  z-index: 998;
  transform: translateY(${({ $shown }) => ($shown ? '0' : '-100%')});
  transition: transform ${SLIDE};
`;

// Giữ chỗ cho banner trong flow để nội dung không bị banner che lúc ở đầu trang
const Spacer = styled.div`
  height: ${APP_BANNER_HEIGHT}px;
`;

// `translate` (không phải `transform`) để không đè transform hide-on-scroll sẵn có của header ở tablet.
// Header khác (LandingLayout) đọc --app-banner-offset: header đó có con position: fixed, translate sẽ nhốt con lại.
const HeaderSlot = styled.div<{ $offset: number }>`
  --app-banner-offset: ${({ $offset }) => $offset}px;
  ${TopNavWrapper} {
    translate: 0 ${({ $offset }) => $offset}px;
    transition:
      translate ${SLIDE},
      transform 0.3s ease-in-out;
  }
`;

// Chỉ setState khi qua ngưỡng → đúng 1 render mỗi lần ẩn/hiện, không chạy theo từng frame
const useShownAtTop = (active: boolean) => {
  const [shown, setShown] = useState(true);
  useEffect(() => {
    if (!active) return undefined;
    const onScroll = () => {
      // body là thứ cuộn khi trang ở quirks mode (index.hbs không có DOCTYPE) → đọc cả hai
      setShown(Math.max(0, window.scrollY || document.body.scrollTop) < APP_BANNER_HEIGHT);
    };
    onScroll();
    // capture: bắt được cả scroll của body (event scroll trên element không bubble)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, [active]);
  return shown;
};

interface Props {
  green: boolean;
  hidden?: boolean;
  appPath: string;
  children: React.ReactNode;
}

const OpenAppBanner = ({ green, hidden, appPath, children }: Props) => {
  const { width } = useContext(Viewport);
  const [dismissed, setDismissed] = useState(() => !shouldShowAppBanner());
  const [popupOpen, setPopupOpen] = useState(false);
  const visible = !dismissed && !hidden && width <= 991;
  const shown = useShownAtTop(visible);

  useEffect(() => {
    const open = () => setPopupOpen(true);
    window.addEventListener(OPEN_APP_POPUP_EVENT, open);
    return () => window.removeEventListener(OPEN_APP_POPUP_EVENT, open);
  }, []);

  return (
    <>
      {visible && <Spacer />}
      {visible && (
        <FixedBar $shown={shown} aria-hidden={!shown}>
          <Bar $green={green}>
            <CloseButton
              type="button"
              aria-label="Close"
              onClick={() => {
                dismissAppBanner();
                setDismissed(true);
              }}
            >
              <CloseOutlined />
            </CloseButton>
            <Texts>
              <Title>
                <FormattedMessage {...messages.title} />
              </Title>
              <Subtitle $green={green}>
                <FormattedMessage {...messages.subtitle} />
              </Subtitle>
            </Texts>
            <Cta type="button" $green={green} onClick={() => openApp(appPath)}>
              <FormattedMessage {...messages.useApp} />
            </Cta>
          </Bar>
        </FixedBar>
      )}
      <HeaderSlot $offset={visible && shown ? APP_BANNER_HEIGHT : 0}>{children}</HeaderSlot>
      {popupOpen && (
        <Overlay onClick={() => setPopupOpen(false)}>
          <PopupBox onClick={(e) => e.stopPropagation()}>
            <PopupTitle>
              <FormattedMessage {...messages.popupTitle} />
            </PopupTitle>
            <PopupActions>
              <PopupButton type="button" onClick={() => setPopupOpen(false)}>
                <FormattedMessage {...messages.popupCancel} />
              </PopupButton>
              <PopupButton
                type="button"
                onClick={() => {
                  setPopupOpen(false);
                  openApp(appPath);
                }}
              >
                <FormattedMessage {...messages.popupOpen} />
              </PopupButton>
            </PopupActions>
          </PopupBox>
        </Overlay>
      )}
    </>
  );
};

export default OpenAppBanner;
